import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAllLeaguesUpcomingEventsQuery } from '@/api/events/query.generated';
import {
    GetAllLeaguesConfigsQueryVariables,
    LeagueConfigsFragment,
    useGetAllLeaguesConfigsQuery,
} from '@/api/leagues/query.generated';
import { STATES } from '@/feature/kyc/const';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useUserStore } from '@/hooks/use-user-store';
import { EventStatus, League } from '@/types/api.generated';
import { nflFullSeason } from '@/utils/league';
import { isEqual } from 'lodash';
import { RequestPolicy, UseQueryArgs } from 'urql';

export const useLeaguesConfigs = (options?: Omit<UseQueryArgs<GetAllLeaguesConfigsQueryVariables>, 'query'>) => {
    const session = useUserStore(state => state.session);
    const guest = useUserStore(state => state.guest);
    return useGetAllLeaguesConfigsQuery({
        ...options,
        requestPolicy: options?.requestPolicy ?? 'cache-first',
        pause: (!session?.access_token && !guest) || options?.pause,
    });
};

type UpcomingEvents = {
    _typename?: string;
    id: string;
    league: League;
    status: EventStatus;
};

export type LeaguePositionFilters = LeagueConfigsFragment['positionFilters'] | undefined;

/**
 * returns the array of leagues enabled & available in the current jurisdiction. All sorted by order
 */
export const useGetOrderedLeagues = (pause = false) => {
    const [{ data, fetching }, refetchLeagues] = useLeaguesConfigs({ pause });
    const jurisdiction = useJurisdictionStore(state => state.jurisdiction);

    const leagues = useMemo(
        () =>
            getActiveLeaguesForJurisdiction(data?.getLeagueConfigs, jurisdiction)
                .filter(league => league.enabled)
                .sort((a, b) => a.order - b.order),

        [data?.getLeagueConfigs, jurisdiction]
    );

    return {
        fetching,
        leagues,
        refetchLeagues,
    };
};

export type SortedLeagues = {
    league: LeagueConfigsFragment;
    eventsCount: number;
    hasLiveEvents: boolean;
};

const MAX_CACHE_TIME = 60 * 1000; //60 seconds

/**
 * hook that returns the leagues sorted by the number of events they have
 * @param pause: boolean - if true, the `useAllLeaguesUpcomingEventsQuery` query will not be executed
 */
export const useGetOrderedLeaguesByEvents = (pause = true, requestPolicy: RequestPolicy = 'cache-and-network') => {
    const [{ data }, execute] = useAllLeaguesUpcomingEventsQuery({
        pause,
        requestPolicy,
    });

    const { leagues, refetchLeagues, fetching } = useGetOrderedLeagues(pause);

    const lastFetch = useRef(0);

    const refetch = useCallback(() => {
        const now = Date.now();
        const delta = now - lastFetch.current;
        if (delta > MAX_CACHE_TIME) {
            lastFetch.current = now;
            refetchLeagues({ requestPolicy });
            execute({ requestPolicy });
        }
    }, [execute, refetchLeagues, requestPolicy]);

    const [sortedLeagues, setSortedLeagues] = useState<SortedLeagues[]>([]);
    const [isSorting, setIsSorting] = useState(true);
    useEffect(() => {
        if (!data?.getUpcomingEventsV2) {
            return;
        }

        const updatedLeagues = sortLeaguesByEvents(leagues, data?.getUpcomingEventsV2);

        if (!isEqual(sortedLeagues, updatedLeagues)) {
            setSortedLeagues([...updatedLeagues]);
        }

        if (data) {
            setIsSorting(false);
        }
    }, [data, leagues, sortedLeagues]);

    return { sortedLeagues, isSorting, fetching, refetch };
};

/**
 *
 * @param {LeagueConfigsFragment[]} leagues - list of all leagues
 * @param events
 * @returns
 */
export const sortLeaguesByEvents = (leagues: LeagueConfigsFragment[], events: UpcomingEvents[] | undefined) => {
    const leaguesWithEvents: SortedLeagues[] = [];
    const emptyLeagues: SortedLeagues[] = [];

    leagues.forEach(league => {
        const count = events?.filter(it => it.league === league.league).length;
        const hasLiveEvents = Boolean(
            events?.some(it => it.league === league.league && it.status === EventStatus.InProgress)
        );

        if (count === 0 && !nflFullSeason(league.league)) {
            emptyLeagues.push({ league, eventsCount: 0, hasLiveEvents });
        } else {
            leaguesWithEvents.push({ league, eventsCount: count ?? 0, hasLiveEvents });
        }
    });

    const updatedLeagues = [...leaguesWithEvents, ...emptyLeagues];

    return updatedLeagues;
};

/**
 * filters all the leagues that are ENABLED & active in the current jurisdiction
 */
export const getActiveLeaguesForJurisdiction = (
    leagueConfigs?: LeagueConfigsFragment[],
    jurisdiction?: string
): LeagueConfigsFragment[] => {
    if (!leagueConfigs || !jurisdiction) {
        return [];
    }

    const enabledLeagues = leagueConfigs.filter(league => league.enabled);
    const enabledLeaguesForJurisdiction = enabledLeagues.filter(league => {
        const enabledForUnknownVal = league.jurisdictions.find(j => j.symbol === 'UNKNOWN');
        const userIsNotInUS = !STATES.find(state => state.value === jurisdiction);
        const enabledForStatesOutsideUS = enabledForUnknownVal && userIsNotInUS;

        const enabledForCurrentState = league.jurisdictions.find(j => j.symbol === jurisdiction);
        return enabledForStatesOutsideUS || enabledForCurrentState;
    });

    return enabledLeaguesForJurisdiction || [];
};
